import React, { useMemo, useRef, useState } from 'react'
import {
  ConfigProvider,
  Divider,
  Dropdown,
  Form,
  type FormInstance,
  type MenuProps,
} from 'antd'
import { useClickAway } from 'ahooks'
import { cls } from 'tagged-classnames-free'
import { Trans, useLingui } from '@lingui/react/macro'

import type { PromptMode } from '@loc/server/interface/task'
import type { MusicGeneratorFormValuesNew } from '@/pages/tad.ai/ai-music-generator/_types/form'

import { CssIcon } from '@/components/CssIcon'
import SmartSvg from '@/assets/svg/smart.svg'
import LightingSvg from '@/assets/svg/lighting.svg'
import { ImageDisplayStatus } from '@/components/ImageDisplay/type'
import { useAiMusicGeneratorStoreFromZustand } from '@/pages/tad.ai/ai-music-generator/_store/createStore'
import { useMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/useMusicFormStore'

export interface IGeneratorBtnActionArea {
  form: FormInstance<MusicGeneratorFormValuesNew>
  throttledHandleClickGenerate: () => void
  imageDisplayStatus: ImageDisplayStatus
}

export const GeneratorBtnActionArea = (props: IGeneratorBtnActionArea) => {
  const { form, throttledHandleClickGenerate, imageDisplayStatus } = props
  const { t } = useLingui()
  const taskType = Form.useWatch('taskType', form) || 'Song'
  const thinking = Form.useWatch('thinking', form) || false
  const imageUrl = Form.useWatch('imageUrl', form)
  const prompt = Form.useWatch('prompt', form)?.trim()
  const promptSelectMode = Form.useWatch('promptSelectMode', form) || 'smart'
  const { isStream } = useAiMusicGeneratorStoreFromZustand()

  const setFormValues = useMusicFormStore((s) => s.setFormValues)

  // 切换think的条件
  const switchThinkCondition = useMemo(() => {
    return taskType === 'Instrumental' && promptSelectMode !== 'quick'
  }, [taskType, promptSelectMode])

  // think按钮不让切换的条件
  const disableThink = useMemo(() => {
    return promptSelectMode === 'quick'
  }, [promptSelectMode])

  // 生成歌词的条件
  // 有prompt 或者 有图片
  const isGenerateLyricsCondition = useMemo(() => {
    return (
      (prompt || imageUrl) && imageDisplayStatus !== ImageDisplayStatus.LOADING
    )
  }, [prompt, imageUrl, imageDisplayStatus])

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const promptSelectModeMenuRef = useRef<HTMLDivElement | null>(null)
  const promptSelectModeRef = useRef<HTMLDivElement | null>(null)

  useClickAway(() => {
    if (dropdownOpen) setDropdownOpen(false)
  }, [promptSelectModeMenuRef, promptSelectModeRef])

  const isSmartMode = useMemo(() => {
    return promptSelectMode === 'smart'
  }, [promptSelectMode])

  const isQuickMode = useMemo(() => {
    return promptSelectMode === 'quick'
  }, [promptSelectMode])

  const promptDropDownItems: Array<{
    key: string
    label: React.ReactNode
    value: string
  }> = [
    {
      key: 'Smart',
      value: t`Smart`,
      label: (
        <div
          className={cls`
            flex
            items-center
            justify-start
            gap-x-2
            rounded-[4px]
            p-2
            ${isSmartMode ? 'bg-f-bg-hover' : ''}
          `}
        >
          <SmartSvg className='size-5' />
          <div className={cls`flex w-[232px] flex-col`}>
            <span className='text-sm font-medium leading-5'>
              <Trans>Smart</Trans>
            </span>
            <span className='text-f-text-quaternary text-xs'>
              <Trans>
                Generate lyrics and style prompts, modify and confirm, then
                generate music
              </Trans>
            </span>
          </div>
          <div
            className={cls`
              ${isSmartMode ? 'size-5 rounded-full bg-white' : ''}
            `}
          >
            <CssIcon
              className={cls`
                size-5
                ${isSmartMode ? 'i-com--circle-check-solid text-f-primary' : 'i-com--circle text-[#FFFFFF24]'}
              `}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'Quick',
      value: t`Quick`,
      label: (
        <div
          className={cls`
            flex
            items-center
            justify-start
            gap-x-2
            rounded-[4px]
            p-2
            ${isQuickMode ? 'bg-f-bg-hover' : ''}
          `}
        >
          <LightingSvg className='size-5' />
          <div className={cls`flex w-[232px] flex-col`}>
            <span className='text-sm font-medium leading-5'>
              <Trans>Quick</Trans>
            </span>
            <span className='text-f-text-quaternary text-xs'>
              <Trans>
                Skip lyrics and style recommendations, directly generate music
              </Trans>
            </span>
          </div>
          <div
            className={cls`
              ${isQuickMode ? 'size-5 rounded-full bg-white' : ''}
            `}
          >
            <CssIcon
              className={cls`
                size-5
                ${isQuickMode ? 'i-com--circle-check-solid text-f-primary' : 'i-com--circle text-[#FFFFFF24]'}
              `}
            />
          </div>
        </div>
      ),
    },
  ]

  const handleDropDownClick: MenuProps['onClick'] = (e) => {
    const { key } = e
    const value = key?.toLowerCase() as PromptMode
    form.setFieldValue('promptSelectMode', value)
    setFormValues({
      promptSelectMode: value as PromptMode,
    })
    if (
      value === 'quick' && // think按钮不可选中
      thinking
    ) {
      form.setFieldValue('thinking', false)

      setFormValues({
        thinking: false,
      })
    }
    // 宏任务，确保表单状态更新完成
    setTimeout(() => {
      setDropdownOpen(false)
    }, 0)
  }

  return (
    <div className='mx-4 my-2 flex items-center justify-between pt-2'>
      <div
        className={cls`flex items-center justify-center gap-1 ${isStream && 'opacity-40'}`}
      >
        <Form.Item noStyle name='taskType'>
          <div
            className={cls`
              border-f-border-secondary
              text-f-text-secondary
              flex h-8
              items-center justify-center gap-1 rounded-[20px] border px-[5px] py-0 sm:px-[14px] sm:py-2
              ${taskType === 'Instrumental' && 'bg-f-secondary-style-1'}
              hover:bg-f-secondary-style-1 hover:text-f-secondary cursor-pointer
            `}
            onClick={() => {
              const type = taskType === 'Song' ? 'Instrumental' : 'Song'
              // 如果是纯音乐模式的话，将thinking设置为false
              if (type === 'Instrumental') {
                form.setFieldValue('thinking', false)
              }
              // 如果切换成音乐模式的话，发现think按钮是false，则设置为true
              if (switchThinkCondition && !thinking) {
                form.setFieldValue('thinking', true)
              }
              form.setFieldValue('taskType', type)
              setFormValues({
                taskType: type,
                thinking: switchThinkCondition,
              })
            }}
          >
            <span
              className={cls`
                i-cus--tad-instrumental size-5
                ${taskType === 'Instrumental' ? 'text-f-secondary' : ''}
              `}
            ></span>
            <span
              className={cls`hidden ${taskType === 'Instrumental' ? 'text-f-secondary' : ''} sm:block`}
            >
              <Trans>Instrumental</Trans>
            </span>
          </div>
        </Form.Item>
        <Form.Item noStyle name='thinking'>
          <div
            className={cls`
              border-f-secondary-style-1
              text-f-text-secondary
              flex
              h-8
              cursor-pointer
              items-center
              justify-center
              gap-1 rounded-full
              border px-[5px] py-0 sm:rounded-[20px] sm:px-[14px]
              sm:py-2
              ${!disableThink && 'hover:bg-f-secondary-style-1 hover:text-f-secondary'}
              ${disableThink && 'opacity-40'}
              ${thinking && 'bg-f-secondary-style-1'}
            `}
            onClick={() => {
              if (disableThink) {
                return
              }
              form.setFieldValue('thinking', !thinking)
              // 跟Instrumental是二选一的关系
              form.setFieldValue('taskType', 'Song')
              setFormValues({
                thinking: !thinking,
                taskType: 'Song',
              })
            }}
          >
            <span
              className={cls`
                i-cus--tad-think size-5
                ${thinking && 'text-f-secondary'}
              `}
            ></span>
            <span
              className={cls`hidden ${thinking ? 'text-f-secondary' : ''} sm:block`}
            >
              <Trans>Think</Trans>
            </span>
          </div>
        </Form.Item>
      </div>
      <div className={cls`flex items-center justify-center gap-1`}>
        <Form.Item
          name='promptSelectMode'
          className={cls`mb-0 ${isStream && 'opacity-40'}`}
        >
          <ConfigProvider
            theme={{
              components: {
                Dropdown: {
                  controlItemBgHover: 'var(--f-bg-hover)',
                  paddingBlock: 0,
                  controlPaddingHorizontal: 0,
                  controlItemBgActive: 'var(--f-bg-hover)',
                  colorText: 'var(--f-text)',
                },
              },
            }}
          >
            <Dropdown
              menu={{
                items: promptDropDownItems,
                onClick: handleDropDownClick,
              }}
              open={dropdownOpen}
              trigger={['click']}
              placement='bottom'
              autoAdjustOverflow
              overlayStyle={{
                top:
                  (promptSelectModeRef?.current?.getBoundingClientRect()
                    ?.bottom ?? 350) + 10,
              }}
              popupRender={(menu) => (
                <div
                  className={cls`
                    [&_.coco-dropdown-menu]:bg-f-bg-container
                    [&_.coco-dropdown-menu]:border-f-bg-active
                    [&_.coco-dropdown-menu-item:last-child]:mt-1
                    [&_.coco-dropdown-menu]:border
                    [&_.coco-dropdown-menu]:p-2
                  `}
                  ref={(node) => {
                    promptSelectModeMenuRef.current = node
                  }}
                >
                  {menu}
                </div>
              )}
            >
              <div
                className='flex items-center'
                onClick={() => {
                  setDropdownOpen(true)
                }}
                ref={promptSelectModeRef}
              >
                <div className='flex cursor-pointer items-center hover:opacity-60'>
                  <span className='leading-5'>
                    {
                      promptDropDownItems.find(
                        (item) =>
                          item.key ===
                          promptSelectMode?.charAt(0)?.toUpperCase() +
                            promptSelectMode?.slice(1),
                      )?.value
                    }
                  </span>
                  <span className='i-cus--tad-down size-5'></span>
                </div>
                <Divider type='vertical' className='mt-[2px] h-4' />
              </div>
            </Dropdown>
          </ConfigProvider>
        </Form.Item>
        <div
          className={`bg-f-primary-style-6 relative z-20 flex size-fit cursor-pointer items-center justify-center rounded-full px-3 py-1.5 transition-opacity active:opacity-40 ${
            isGenerateLyricsCondition ? 'opacity-100' : 'opacity-40'
          }`}
          onClick={throttledHandleClickGenerate}
        >
          {isStream ? t`Stop` : t`Generate`}
        </div>
      </div>
    </div>
  )
}
